import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { Star, StarOutlineOutlined, XIcon } from 'components/icons';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { PinMark } from './Pinned';
import { setPinDragChip, usePinDragState } from './usePinDrag';

/** The puff's six dots, blown 10px outwards, 60° apart. */
const PUFF_DOTS = Array.from({ length: 6 }, (_, dot) => {
    const angle = (dot * Math.PI) / 3;
    return { key: dot, x: `${Math.round(Math.cos(angle) * 10)}px`, y: `${Math.round(Math.sin(angle) * 10)}px` };
});

/**
 * The chip a pin is dragged as, in a portal over everything: it follows the pointer, tilted, until Pinned's magnet
 * docks it on the landing row, where it wiggles once and its star fills. A pin dragged out of reach turns red with
 * Unpin. `usePinDrag` moves it; this draws its states and how it goes.
 */
export default function PinDragChip() {
    const { drag } = usePinDragState();
    if (!drag) return null;
    const { pin, phase, docked } = drag;
    const unpinning = drag.from >= 0 && !docked;

    return createPortal(
        <div
            ref={setPinDragChip}
            aria-hidden
            className="pointer-events-none fixed top-0 left-0 z-(--z-tooltip) will-change-transform"
        >
            <div
                // Remounted on each docking, which plays the wiggle once.
                key={drag.docks}
                className={cn(
                    // Edged by its elevation's ring; a state's edge colour is a ring in the same place.
                    'text-foreground text-meta flex items-center gap-2 rounded-lg ps-2 pe-2.5 font-medium whitespace-nowrap outline-hidden',
                    'shadow-drag origin-[33%_50%] transition-opacity duration-150 motion-safe:transition-[rotate,scale,opacity] motion-safe:duration-180',
                    docked
                        ? 'ring-changed-border bg-card h-7 w-full ring-1 pointer-coarse:h-11'
                        : 'h-8 motion-safe:-rotate-2',
                    docked && phase === 'drag' && 'pin-wiggle',
                    unpinning ? 'bg-danger-tint ring-destructive/40 ring-1' : !docked && 'bg-card',
                    phase === 'land' && 'opacity-0 motion-safe:scale-90',
                    phase === 'cancel' && 'opacity-0 duration-120 motion-safe:duration-120',
                    phase === 'puff' && 'pin-puff motion-reduce:hidden'
                )}
            >
                <PinMark pin={pin} />
                <span className="max-w-[280px] min-w-0 flex-1 truncate">{pin.label}</span>
                {unpinning ? (
                    <span className="text-destructive text-caption flex items-center gap-1 font-semibold">
                        <span className="bg-destructive text-destructive-foreground flex size-3.5 items-center justify-center rounded-full">
                            <XIcon className="size-2.5" />
                        </span>
                        {t('pins.unpin')}
                    </span>
                ) : docked ? (
                    <Star className="text-secondary size-[15px]" />
                ) : (
                    <StarOutlineOutlined className="text-muted-foreground size-[15px]" />
                )}
            </div>
            {phase === 'puff' && (
                <span className="absolute inset-0 motion-reduce:hidden">
                    {PUFF_DOTS.map(({ key, x, y }) => (
                        <span
                            key={key}
                            className="pin-puff-dot bg-border-dashed absolute top-1/2 left-1/2 size-1.5 rounded-full"
                            style={{ '--pin-dot-x': x, '--pin-dot-y': y } as CSSProperties}
                        />
                    ))}
                </span>
            )}
        </div>,
        document.body
    );
}
